//Import redux types
import {
  GET_FEED_POST_ERROR,
  REMOVE_INVALID_POST,
  FETCH_FEED_POST,
  SET_FEED_POST,
  UPDATE_POST_CACHE_INTERACTIONS,
  GET_HOME_FEED,
  HOME_FEED_ERROR,
  GET_POST_COMMENTS,
  GET_POST_COMMENTS_ERROR,
  GET_POST_COMMENTS_RESET,
  SET_HOME_FEED,
  SET_POST_INTERACTIONS,
  REFRESH_HOME_FEED,
  FEED_DELETE_COMMENT,
  COMMENT_LIKE,
  COMMENT_LIKE_ERROR,
  //Dual Feed System
  SET_FEED_TYPE,
  GET_FOLLOWING_FEED,
  GET_TRENDING_FEED,
  REFRESH_FOLLOWING_FEED,
  REFRESH_TRENDING_FEED,
  SET_FOLLOWING_FEED,
  SET_TRENDING_FEED,
  FOLLOWING_FEED_ERROR,
  TRENDING_FEED_ERROR,
} from "../actions/types";

//Initial state object
const initialState = {
  // Comments state (used by PostCommentModal)
  comments: [],
  commentsLoading: true,
  commentsError: false,

  // Feed system
  feeds: {
    currentFeedType: "trending", // 'following' | 'trending'
    feedTypeLoading: true, // Loading state for feed type initialization
    following: {
      posts: null,
      loading: false,
      error: false,
      offset: 0,
      isEndOfFeed: false,
      numPosts: null,
      lastFetched: null,
    },
    trending: {
      posts: null,
      loading: false,
      error: false,
      offset: 0,
      isEndOfFeed: false,
      numPosts: null,
      lastFetched: null,
    },
  },
  // Normalized cache of fully-loaded posts keyed by post id
  postCache: {
    // Structure: { [postId]: { data: postData, expiry: timestamp, reposts: { [repostUserId]: repostData } } }
    byId: {},
    // Track errored posts to prevent immediate refetch
    errors: {},
  },
};

export default function (state = initialState, action) {
  //Destructure action
  const { type, payload } = action;

  switch (type) {
    case FEED_DELETE_COMMENT:
      // Comment count updates are now handled in the cache system
      // No need to update legacy posts state
      return state;
    case FETCH_FEED_POST:
      return {
        ...state,
        // Update current feed's posts to show loading state
        feeds: {
          ...state.feeds,
          [state.feeds.currentFeedType]: {
            ...state.feeds[state.feeds.currentFeedType],
            posts: state.feeds[state.feeds.currentFeedType].posts
              ? state.feeds[state.feeds.currentFeedType].posts.map((post, index) => {
                  if (index === payload.index) {
                    // Preserve existing postData when setting loading state
                    return { ...post, loading: true, error: false };
                  }
                  return post;
                })
              : null,
          },
        },
      };
    case SET_FEED_POST:
      return {
        ...state,
        // Update current feed's posts (remove postData storage - use cache instead)
        feeds: {
          ...state.feeds,
          [state.feeds.currentFeedType]: {
            ...state.feeds[state.feeds.currentFeedType],
            posts: state.feeds[state.feeds.currentFeedType].posts
              ? state.feeds[state.feeds.currentFeedType].posts.map((post, index) => {
                  if (index === payload.index) {
                    return { ...post, error: false, loading: false }; // Remove postData storage
                  }
                  return post;
                })
              : null,
          },
        },
        // Cache the fully fetched post by id to avoid refetching
        postCache: (() => {
          const data = payload?.data || {};
          const postId = data.id || data._id || data.postid;
          if (!postId) return state.postCache;

          // Split base and repost context for caching
          const { repost, ...base } = data;
          const existingCache = state.postCache.byId[postId] || { reposts: {} };

          // Update cache with new data, expiry, and repost info
          const updatedCache = {
            ...state.postCache.byId,
            [postId]: {
              data: base,
              expiry: Date.now() + 5 * 60 * 1000, // 5 minutes from now
              reposts: {
                ...existingCache.reposts,
                ...(repost && repost.user ? { [repost.user]: repost } : {}),
              },
            },
          };

          return {
            byId: updatedCache, // Keep all cached posts - prefer stale data over refetching
            errors: state.postCache.errors, // Don't clean errors on successful fetch
          };
        })(),
      };
    case UPDATE_POST_CACHE_INTERACTIONS:
      return {
        ...state,
        postCache: {
          ...state.postCache,
          byId: {
            ...state.postCache.byId,
            [payload.postId]: state.postCache.byId[payload.postId]
              ? {
                  ...state.postCache.byId[payload.postId],
                  data: {
                    ...state.postCache.byId[payload.postId].data,
                    interactions: {
                      ...state.postCache.byId[payload.postId].data.interactions,
                      ...payload.interactionUpdates,
                    },
                  },
                }
              : null,
          },
        },
      };
    case GET_FEED_POST_ERROR:
      return {
        ...state,
        // Update current feed's posts (remove postData storage - use cache instead)
        feeds: {
          ...state.feeds,
          [state.feeds.currentFeedType]: {
            ...state.feeds[state.feeds.currentFeedType],
            posts: state.feeds[state.feeds.currentFeedType].posts
              ? state.feeds[state.feeds.currentFeedType].posts.map((post, index) => {
                  if (index === payload.index) {
                    return { ...post, error: true, loading: false }; // Remove postData storage
                  }
                  return post;
                })
              : null,
          },
        },
        // Track errored posts to prevent immediate refetch (30 second cooldown)
        postCache: {
          ...state.postCache,
          errors: {
            ...state.postCache.errors,
            [payload.postid]: Date.now() + 2 * 60 * 1000, // 2 minutes from now
          },
        },
      };
    case REMOVE_INVALID_POST:
      return {
        ...state,
        // Remove invalid post from current feed
        feeds: {
          ...state.feeds,
          [state.feeds.currentFeedType]: {
            ...state.feeds[state.feeds.currentFeedType],
            posts: state.feeds[state.feeds.currentFeedType].posts
              ? state.feeds[state.feeds.currentFeedType].posts.filter((post, idx) => idx !== payload.index)
              : null,
          },
        },
        // Clean up cache entries for the removed post
        postCache: {
          ...state.postCache,
          byId: Object.fromEntries(Object.entries(state.postCache.byId).filter(([id]) => id !== payload.postid)),
          errors: Object.fromEntries(Object.entries(state.postCache.errors).filter(([id]) => id !== payload.postid)),
        },
      };
    case GET_POST_COMMENTS:
      return {
        ...state,
        comments: payload,
        commentsLoading: false,
        commentsError: false,
      };
    case GET_POST_COMMENTS_ERROR:
      return {
        ...state,
        comments: [],
        commentsLoading: false,
        commentsError: true,
      };
    case GET_POST_COMMENTS_RESET:
      return {
        ...state,
        comments: [],
        commentsLoading: true,
        commentsError: false,
      };
    case COMMENT_LIKE:
      return {
        ...state,
        comments: state.comments.map((comment) => {
          if (comment._id === payload.commentid) {
            console.log("Updating comment:", comment._id, "with hasLiked:", payload.liked);
            return {
              ...comment,
              hasLiked: payload.liked,
              likes: Array(payload.likesCount)
                .fill()
                .map(() => ({ user: null })),
            };
          }
          return comment;
        }),
      };
    case COMMENT_LIKE_ERROR:
      // Reset the comment like state on error using previous state
      return {
        ...state,
        comments: state.comments.map((comment) => {
          if (comment._id === payload.commentid && payload.previousState) {
            return {
              ...comment,
              hasLiked: payload.previousState.hasLiked,
              likes: Array(payload.previousState.likesCount)
                .fill()
                .map(() => ({ user: null })),
            };
          }
          return comment;
        }),
      };
    case SET_POST_INTERACTIONS:
      // Post interactions are now handled in the cache system
      // No need to update legacy posts state
      return state;

    // Dual Feed System Cases
    case SET_FEED_TYPE:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          currentFeedType: payload,
          feedTypeLoading: false, // Feed type is now initialized
        },
      };

    case GET_FOLLOWING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          following: {
            ...state.feeds.following,
            loading: true,
            error: false,
          },
        },
      };

    case GET_TRENDING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          trending: {
            ...state.feeds.trending,
            loading: true,
            error: false,
          },
        },
      };

    case REFRESH_FOLLOWING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          following: {
            ...state.feeds.following,
            loading: true,
            lastFetched: Date.now(),
            // Don't clear posts or cache - just reset pagination
            offset: 0,
            isEndOfFeed: false,
          },
        },
      };

    case REFRESH_TRENDING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          trending: {
            ...state.feeds.trending,
            loading: true,
            lastFetched: Date.now(),
            // Don't clear posts or cache - just reset pagination
            offset: 0,
            isEndOfFeed: false,
          },
        },
      };

    case SET_FOLLOWING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          following: {
            ...state.feeds.following,
            posts: payload.posts,
            loading: false,
            error: false,
            offset: payload.offset,
            isEndOfFeed: payload.isEndOfFeed,
            numPosts: payload.numPosts,
            lastFetched: Date.now(),
          },
        },
      };

    case SET_TRENDING_FEED:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          trending: {
            ...state.feeds.trending,
            posts: payload.posts,
            loading: false,
            error: false,
            offset: payload.offset,
            isEndOfFeed: payload.isEndOfFeed,
            numPosts: payload.numPosts,
            lastFetched: Date.now(),
          },
        },
      };

    case FOLLOWING_FEED_ERROR:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          following: {
            ...state.feeds.following,
            posts: null,
            loading: false,
            error: true,
          },
        },
      };

    case TRENDING_FEED_ERROR:
      return {
        ...state,
        feeds: {
          ...state.feeds,
          trending: {
            ...state.feeds.trending,
            posts: null,
            loading: false,
            error: true,
          },
        },
      };

    // Removed CLEANUP_CACHE - no auto-cleanup, let new fetches populate old ones

    default:
      return state;
  }
}
